import { Button, cn } from '@repo/ui';
import { Menu, Search } from 'lucide-react';
import type { ReactElement } from 'react';
import { ThemeToggle } from '@/components/layout/ThemeToggle';
import { NotificationBell } from '@/components/NotificationBell';
import { CalculatorButton } from '@/features/calculator/components/CalculatorButton';
import { ExchangeRateBadge } from './ExchangeRateBadge';
import { TenantSwitcher } from './TenantSwitcher';

/**
 * Top bar — sticky, h-11 (44 px). Holds four slots left-to-right:
 *   1. Hamburger (mobile only, `lg:hidden`)
 *   2. Brand mark + tenant name (visible at all sizes — the brand
 *      gives the page a clear owner on narrow screens where the
 *      sidebar is hidden)
 *   3. Spacer (`flex-1`)
 *   4. TenantSwitcher + bell + sign-out
 *
 * The bar uses `bg-background/80 backdrop-blur` so content peeks
 * through when the page scrolls — gives a polished native-app feel
 * without spending pixels on a real translucency stack.
 */
export interface TopBarProps {
  onOpenSidebar: () => void;
  isMobileSidebarOpen: boolean;
}

export function TopBar({ onOpenSidebar, isMobileSidebarOpen }: TopBarProps): ReactElement {
  return (
    <header
      className={cn(
        'sticky top-0 z-30 flex h-11 shrink-0 items-center gap-2 border-b border-border bg-background/80 px-3 backdrop-blur sm:px-4',
      )}
    >
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onOpenSidebar}
        aria-label={isMobileSidebarOpen ? 'Cerrar menú' : 'Abrir menú'}
        aria-expanded={isMobileSidebarOpen}
        className="lg:hidden"
      >
        <Menu className="size-4" aria-hidden="true" />
      </Button>

      <div className="flex items-center gap-2 lg:hidden">
        <span className="text-sm font-semibold tracking-tight">GlobalesERP</span>
      </div>

      <div className="ml-2 hidden flex-1 items-center gap-2 rounded-md bg-muted/40 px-3 py-1.5 text-sm text-muted-foreground lg:flex">
        <Search className="size-3.5" aria-hidden="true" />
        <span className="truncate">Buscar clientes, productos, órdenes…</span>
        <span className="ml-auto rounded bg-background px-1.5 py-px font-mono text-[10px]">⌘K</span>
      </div>

      <div className="ml-auto flex items-center gap-1.5">
        <TenantSwitcher />
        <ExchangeRateBadge />
        <CalculatorButton />
        <ThemeToggle />
        <NotificationBell />
      </div>
    </header>
  );
}
